import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CompareFunction } from '../interfaces/compare-function';
import { filter, take, tap } from 'rxjs';
import { AfterViewInit, Directive, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { merge, Observable, of, Subject } from 'rxjs';
import { MatSort, Sort } from '@angular/material/sort';
import { MatProgressBar } from '@angular/material/progress-bar';
import { TableDataSource } from './table-data-source';
import { LocalStorageService } from 'ngx-webstorage';
import { SelectionModel } from '@angular/cdk/collections';

export interface PaginationEvent {
  pageIndex: number;
  pageSize: number;
  length: number;
}

export interface IColConfig {
  name: string;
  class: string;
  show: boolean;
  disabled?: boolean;
}

export interface TableColFilterChange {
  cols: IColConfig[];
}

@Directive()
export abstract class TableComponent<T, D extends TableDataSource<T>>
  implements AfterViewInit, OnInit
{
  @ViewChild(MatPaginator, { static: true, read: MatPaginator })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: true, read: MatSort }) sort: MatSort;

  @ViewChild(MatProgressBar, { static: true, read: MatProgressBar })
  progressBar: MatProgressBar;

  private tableLoadingSubject: Subject<boolean> = new Subject();

  tableLoading$: Observable<boolean>;

  abstract dataSource: D;

  abstract storageKey: string;

  abstract disableColumns: any[];

  abstract displayedColumns: string[];

  readonly COLS_STORAGE_KEY = 'colsConfRun';

  readonly ITEMS_PER_PAGE_STORAGE_KEY = 'itemsPerPage';

  readonly SORT_STORAGE_KEY = 'sortConf';

  readonly limitsList: number[] = [5, 10, 20, 50, 100];

  selection: SelectionModel<T>;

  itemsPerPage = 5;

  colsConfRun: IColConfig[];

  colsToShow;

  sortConf: { [key: string]: CompareFunction<T> } = {};

  colsShowConfig: Map<string, string> = new Map();

  protected colsConf: IColConfig[] = [];

  protected storage: LocalStorageService;

  defaultSortEvent: Sort;

  currentSortEvent: Sort;

  abstract update(): void;

  abstract initDataSource(): void;

  ngOnInit() {
    this.initCols();
    this.initItemsPerPage();
    this.initSortConf();
  }

  dropEvent(event: CdkDragDrop<string[]> | any) {
    const name = this.storageKey;
    const getSaveCol = JSON.parse(localStorage.getItem(name));

    if (event) {
      localStorage.setItem('fixPosition', 'true');
      if (this.disableColumns[event.currentIndex].disable !== true) {
        moveItemInArray(
          this.displayedColumns,
          event.previousIndex,
          event.currentIndex,
        );

        if (getSaveCol || localStorage.getItem('fixPosition')) {
          localStorage.setItem(name, JSON.stringify(this.displayedColumns));
        } else {
          localStorage.setItem('fixPosition', 'false');
        }
      } else {
        return false;
      }
    }
  }

  onColsChange($event: TableColFilterChange): void {
    this.colsConfRun = $event.cols;
    localStorage.setItem('fixPosition', 'false');
    const hashCols = {};
    this.colsConfRun.forEach((col) => (hashCols[col.class] = col.show));
    this.storage.store(
      this.makeLocalStorageName(this.COLS_STORAGE_KEY),
      hashCols,
    );
    this.generateColsClassesForHiding();

    if (this.currentSortEvent) {
      this.restoreSort(this.currentSortEvent, true);
    }
  }

  onPageChange($event: PaginationEvent): void {
    this.itemsPerPage = $event.pageSize;
    this.storage.store(
      this.makeLocalStorageName(this.ITEMS_PER_PAGE_STORAGE_KEY),
      this.itemsPerPage,
    );
  }

  onSortChange(event: Sort): void {
    this.currentSortEvent = event;
  }

  // This function visually restores the selected sort.
  protected restoreSort(sort: Sort, withTimeout: boolean = false): void {
    if (!sort) {
      console.warn('No active Sort provided!');
      return;
    }

    const { active, direction } = sort;

    if (!active || !direction) {
      console.warn('No Sort active or direction!');
      return;
    }

    const restore = () => {
      this.sort.active = active;
      this.sort.direction = direction;
      this.sort.sortChange.emit({ active, direction });
    };

    if (withTimeout) {
      // setTimeout is used to properly restore the selected sort indicator when column visibility changes
      setTimeout(restore);
    } else {
      restore();
    }
  }

  protected initItemsPerPage(): void {
    this.itemsPerPage =
      this.storage.retrieve(
        this.makeLocalStorageName(this.ITEMS_PER_PAGE_STORAGE_KEY),
      ) || this.itemsPerPage;
  }

  protected initSortConf(): void {
    const sortConf = this.storage.retrieve(
      this.makeLocalStorageName(this.SORT_STORAGE_KEY),
    );

    if (sortConf && sortConf.direction !== '' && sortConf.active !== '') {
      this.currentSortEvent = sortConf;
    } else {
      this.currentSortEvent = this.defaultSortEvent;
    }
  }

  protected makeLocalStorageName(key): string {
    return `${this.storageKey}_${key}`;
  }

  protected initCols(): void {
    // Initially set columns from STATIC CONFIG
    this.colsConfRun = JSON.parse(JSON.stringify(this.colsConf));
    // Check if there are any columns in local storage
    const hashCols = this.storage.retrieve(
      this.makeLocalStorageName(this.COLS_STORAGE_KEY),
    );
    // If there are columns in local storage, set their visibility
    // mapping STATIC CONFIG over local storage items,
    // so if the column is not in STATIC CONFIG, it will be skipped
    if (hashCols) {
      this.colsConfRun = this.colsConfRun.map((col) => {
        // Class here is actually the name of the column
        if (hashCols[col.class] !== undefined) {
          col.show = hashCols[col.class] || false;
        } else {
          col.show = true;
        }
        if (col.disabled) {
          col.show = col.disabled;
        }
        return col;
      });
    }
    // Further processing of columns
    // actually the naming of this function is not correct
    // because it does not generate classes for hiding
    // but rather sets the order of columns
    // and saves the order in local storage
    this.generateColsClassesForHiding();
  }

  private generateColsClassesForHiding(): void {
    const name = this.storageKey;

    if (this.displayedColumns.length < 1) {
      console.warn('wrong column definition');
      return;
    }

    if (this.displayedColumns[this.displayedColumns.length - 1] === 'options') {
      if (this.displayedColumns.length < 2) {
        console.warn('wrong column definition for table with options');
        return;
      }
    }

    if (!this.colsConfRun.length || this.colsConfRun.length === 0) {
      return;
    }

    const colsBegin = [];

    const colsEnd = [];

    // Set positions of FIXED columns
    // The config is usually located in a generic table component
    // or in a specific table component
    //
    // At this moment of time this.displayedColumns is equal to
    // the STATIC CONFIG (in each table component)
    for (const colName of this.displayedColumns) {
      const colPosition = this.colsShowConfig.get(colName);

      if (colPosition && colPosition === 'begin') {
        colsBegin.push(colName);
      }

      if (colPosition && colPosition === 'end') {
        colsEnd.push(colName);
      }
    }

    // ColsConfRun looks like this:
    //
    // [
    //   {
    //     "name": "Номер реєстру",
    //     "show": false,
    //     "class": "scanSheetNumber"
    //   },
    //   {
    //     "name": "Дата створення",
    //     "show": true,
    //     "class": "date"
    //   },
    //
    // Taking Object.keys(colsConfRun) and filtering by show property
    // Then mapping to class property
    // (which actually matches the column name and this.displayedColumns format)
    // Better alternative would be to use
    // this.colsToShow = this.colsConfRun
    //   .filter(col => col.show)
    //   .map(col => col.class);
    this.colsToShow = Object.keys(this.colsConfRun)
      .filter((key) => this.colsConfRun[key].show)
      .map((key) => this.colsConfRun[key].class);

    const localStorageColumns = JSON.parse(localStorage.getItem(this.storageKey)) ?? [];

    // Add new visible columns to the end
    this.colsConfRun.forEach(col => {
      if (col.show && !localStorageColumns.includes(col.class)) {
        localStorageColumns.push(col.class);
      }
    });

    const referenceOrder = Array.isArray(localStorageColumns) && localStorageColumns.length > 0
      ? localStorageColumns
      : this.displayedColumns;

    // Reordering columns according to the order in local storage
    const reorderedColumns = referenceOrder.filter(col => this.colsToShow.includes(col));
    //
    // Now gluing all together:
    // colsBegin(fixed) + colsToShow + colsEnd(fixed)

    // After this line this.displayedColumns will be equal to
    // the new modified order of columns
    //
    this.displayedColumns = [...colsBegin, ...reorderedColumns, ...colsEnd];

    if (
      localStorage.getItem('fixPosition') !== 'true' ||
      localStorage.getItem('fixPosition') === 'null'
    ) {
      localStorage.setItem(name, JSON.stringify(this.displayedColumns));
    }
  }

  ngAfterViewInit() {
    this.initPaginator();
    this.initLoading();
    this.onDataSourceReady();
  }

  initPaginator(): void {
    if (this.paginator) {
      this.initPageUpdate();
      this.initCheckEmptyPage();
    }
  }

  initLoading(): void {
    const tableLoading$ = !!this.progressBar
      ? this.tableLoadingSubject.asObservable()
      : of(false);
    const datasourceLoading$ = this.dataSource.loading$;
    this.tableLoading$ = merge(tableLoading$, datasourceLoading$).pipe();
  }

  tableLoading(value: boolean): void {
    this.tableLoadingSubject.next(value);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.getSnapshot().length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource
          .getSnapshot()
          .forEach((row) => this.selection.select(row));
  }

  initPageUpdate(): void {
    this.paginator.page.pipe(tap(() => this.update())).subscribe();
  }

  initCheckEmptyPage(): void {
    this.dataSource.totalInCurrentSearch$
      .pipe(filter((totalInCurrentSearch) => !totalInCurrentSearch))
      .subscribe(() => this.setPreviousPage());
  }

  private setPreviousPage(): void {
    if (!this.paginator.hasPreviousPage()) {
      return;
    }
    this.paginator.previousPage();
  }

  sortData(event: Sort): void {
    this.currentSortEvent = event;

    if (event.direction === '') {
      this.dataSource.resetSort();
    } else {
      const compareFn = this.sortConf[event.active];
      if (!compareFn) {
        console.warn(`No sort compareFn found for column "${event.active}"`);
        return;
      }
      this.dataSource.sort(
        compareFn,
        event.direction === 'asc',
      );
    }
    this.storage.store(this.makeLocalStorageName(this.SORT_STORAGE_KEY), event);
  }

  onFilterFnChanged(fil: (...args) => boolean): void {
    this.dataSource.filter = fil || null;
  }

  protected onDataSourceReady(): void {
    if (this.dataSource) {
      this.dataSource.loading$
        .pipe(
          filter((loading) => !loading),
          take(1),
        )
        .subscribe(() => {
          if (this.currentSortEvent) {
            this.restoreSort(this.currentSortEvent);
          }
        });
    }
  }
}
